import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Bell, Loader2, Plus, Trash2 } from "lucide-react";
import { ConsoleShell } from "@/components/ConsoleShell";
import { useVehicles } from "@/context/VehicleContext";
import { displayVehicleName } from "@/contracts/vehicle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Garage() {
  const navigate = useNavigate();
  const { vehicles, loading, refresh, notifications, setNotifications } = useVehicles();
  const [legacy, setLegacy] = useState([]);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!loading && vehicles.length === 0) {
      axios
        .get(`${API}/garage/bookmarks`)
        .then((r) => setLegacy(r.data || []))
        .catch(() => setLegacy([]));
    }
  }, [loading, vehicles.length]);

  const remove = async (id) => {
    setBusyId(id);
    try {
      await axios.delete(`${API}/vehicles/${id}`);
      await refresh();
    } finally {
      setBusyId(null);
    }
  };

  const markRead = async (id) => {
    await axios.post(`${API}/notifications/${id}/read`);
    setNotifications((ns) => ns.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  return (
    <ConsoleShell title="garage">
      <div className="mx-auto max-w-[1500px] px-6 lg:px-10 pt-10 pb-24">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <span className="chip-brand">/ garage · owned vehicles</span>
          <div className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter">
                Your garage.
              </h1>
              <p className="mt-3 text-white/55 max-w-xl text-sm">
                Registered vehicles with locked AI context, maintenance, recalls, and service history.
              </p>
            </div>
            <Link
              to="/console/register"
              className="inline-flex items-center gap-2 h-11 px-5 bg-mclaren text-void font-mono-tech text-[11px] uppercase tracking-widest hover:bg-sunset"
            >
              <Plus className="h-4 w-4" /> register vehicle
            </Link>
          </div>
        </motion.div>

        {notifications.length > 0 && (
          <div className="mt-8 border border-white/10 bg-obsidian/60 p-4">
            <div className="flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest text-white/50 mb-3">
              <Bell className="h-3.5 w-3.5" /> notifications
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {notifications.slice(0, 8).map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => markRead(n.id)}
                  className={`w-full text-left px-3 py-2 border text-sm ${
                    n.read ? "border-white/5 text-white/40" : "border-cyan-electric/30 text-white/80"
                  }`}
                >
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-cyan-electric">
                    {n.kind}
                  </div>
                  <div>{n.title}</div>
                  <div className="text-white/50 text-xs">{n.body}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10">
          {loading ? (
            <div className="py-20 flex justify-center font-mono-tech text-[11px] uppercase tracking-widest text-white/40">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> loading garage...
            </div>
          ) : vehicles.length === 0 ? (
            <div className="border border-dashed border-white/15 py-20 text-center">
              <div className="font-display text-2xl">No registered vehicles yet.</div>
              <p className="mt-3 text-white/50 text-sm max-w-md mx-auto">
                Register a vehicle to unlock the dashboard, AI agents, maintenance, and recalls.
              </p>
              <Link
                to="/console/register"
                className="mt-8 inline-flex h-11 px-5 bg-mclaren text-void font-mono-tech text-[11px] uppercase tracking-widest items-center"
              >
                Start registration
              </Link>
              {legacy.length > 0 && (
                <div className="mt-12 text-left max-w-2xl mx-auto">
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40 mb-3">
                    Legacy catalog bookmarks - register to upgrade
                  </div>
                  <div className="grid gap-2">
                    {legacy.map((c) => (
                      <Link
                        key={c.wd_id}
                        to={`/console/register?wd_id=${c.wd_id}`}
                        className="border border-white/10 px-4 py-3 flex justify-between hover:border-cyan-electric"
                      >
                        <span>
                          {c.brand} {c.model}
                        </span>
                        <span className="font-mono-tech text-[10px] uppercase text-cyan-electric">
                          register →
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {vehicles.map((v, i) => (
                <motion.article
                  key={v.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="group relative border border-white/10 bg-obsidian/70 overflow-hidden hover:border-white/25"
                >
                  <button
                    type="button"
                    className="absolute inset-0 z-10"
                    onClick={() => navigate(`/console/vehicle/${v.id}`)}
                    aria-label={`Open ${displayVehicleName(v)}`}
                  />
                  <div className="aspect-[16/10] bg-black relative">
                    {v.image_url ? (
                      <img
                        src={v.image_url}
                        alt=""
                        referrerPolicy="no-referrer"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center font-display text-5xl text-white/20">
                        {v.brand?.[0]}
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-obsidian to-transparent" />
                    {v.is_primary && (
                      <span className="absolute top-3 left-3 font-mono-tech text-[9px] uppercase tracking-widest px-2 py-1 border border-cyan-electric text-cyan-electric bg-black/60">
                        primary
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(v.id);
                      }}
                      disabled={busyId === v.id}
                      className="absolute top-3 right-3 z-20 h-8 w-8 flex items-center justify-center border border-white/15 bg-black/60 text-white/60 hover:text-mclaren hover:border-mclaren opacity-0 group-hover:opacity-100"
                    >
                      {busyId === v.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                  <div className="p-5 relative z-0">
                    <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40">
                      {v.year} · {v.brand}
                    </div>
                    <div className="mt-1 font-display text-xl tracking-tighter">{displayVehicleName(v)}</div>
                    <div className="mt-3 flex justify-between font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                      <span>{Number(v.mileage || 0).toLocaleString()} mi</span>
                      <span className="text-cyan-electric">health {v.health_score ?? "-"}</span>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </div>
    </ConsoleShell>
  );
}
